import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PublicShell } from "../components/PublicShell";
import { Button } from "../components/ui/button";
import { api } from "../lib/api";
import { useReveal } from "../lib/useReveal";
import { ArrowRight, Star, Wrench, Gavel, FileSignature, Sparkles, Gauge, Fuel, Download } from "lucide-react";

export default function PublicHomePage() {
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [auctions, setAuctions] = useState([]);
  const [pillarsRef, pillarsVis] = useReveal();
  const [invRef, invVis] = useReveal({ threshold: 0.05 });
  const [revRef, revVis] = useReveal();
  const [auctionRef, auctionVis] = useReveal();
  const [ctaRef, ctaVis] = useReveal();

  useEffect(() => {
    api.get("/vehicles", { params: { status: "available" } }).then((r) => setVehicles((r.data || []).slice(0, 6)));
    api.get("/reviews").then((r) => setReviews(r.data || []));
    api.get("/auctions", { params: { status: "live" } }).then((r) => setAuctions((r.data || []).slice(0, 3)));
  }, []);

  return (
    <PublicShell transparent>
      {/* HERO */}
      <section className="hero-bg stripe-overlay pt-32 pb-28 min-h-[85vh] flex items-center">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 w-full grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 fade-up">
            <div className="brand-divider mb-4 max-w-md">
              <span className="dot" />
              <span className="label-eyebrow" style={{ color: "var(--wakzy-gold)" }}>
                Established · Fayetteville, GA
              </span>
              <span className="dot" />
            </div>
            <h1 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl leading-tight mb-8 chrome-text inline-block tracking-tight" data-testid="company-name-hero">
              Wakzy Auto Sales &amp; Services LLC
            </h1>
            <h2 className="font-heading font-light text-5xl sm:text-6xl lg:text-7xl leading-[1.02] mb-2">
              <span className="chrome-text">Luxury vehicles.</span>
            </h2>
            <h2 className="font-display text-7xl sm:text-8xl lg:text-[8.5rem] leading-[0.95] tracking-tight mb-2 -ml-1">
              <span className="tri-text">PAPERLESS.</span>
            </h2>
            <h2 className="font-heading font-light text-5xl sm:text-6xl lg:text-7xl leading-[1.02] mb-8">
              <span className="chrome-text">Always.</span>
            </h2>
            <p className="text-lg text-slate-300 max-w-xl leading-relaxed mb-10">
              Browse our curated inventory, get pre-approved for financing, bid in live auctions, or schedule service — all from one platform.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button size="lg" onClick={() => navigate("/inventory")} className="btn-cta rounded-sm h-14 px-8 text-base" data-testid="hero-shop-btn">
                Shop Inventory <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button size="lg" variant="outline" onClick={() => navigate("/financing")} className="rounded-sm h-14 px-8 text-base border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10 bg-transparent" data-testid="hero-finance-btn">
                Get Pre-Approved
              </Button>
              <Button size="lg" variant="outline" onClick={() => navigate("/install")} className="rounded-sm h-14 px-8 text-base border-white/20 text-white hover:bg-white/10 bg-transparent" data-testid="hero-install-btn">
                <Download className="w-5 h-5 mr-2" /> Download App
              </Button>
            </div>
          </div>
          <div className="lg:col-span-5 hidden lg:flex justify-center">
            <div className="relative float-slow">
              <div className="absolute -inset-10 rounded-full blur-3xl opacity-50" style={{ background: "radial-gradient(circle, rgba(230,57,70,0.4) 0%, rgba(212,175,55,0.25) 40%, transparent 70%)" }} />
              <div className="gradient-border" style={{ borderRadius: "24px" }}>
                <div className="gradient-border-inner p-10" style={{ borderRadius: "23px", background: "rgba(4,6,13,0.85)" }}>
                  <img src="https://customer-assets.emergentagent.com/job_1be79f83-3969-45b0-8e96-f7609932169e/artifacts/rxkk1yi8_0E7539D4-89C8-4BC5-A95C-974033632CC9.png" alt="Wakzy" className="h-56 w-auto logo-glow" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="py-24 border-t border-white/5" ref={pillarsRef}>
        <div className={`max-w-7xl mx-auto px-6 lg:px-8 reveal ${pillarsVis ? "is-visible" : ""}`}>
          <div className="brand-divider max-w-xs mb-4"><span className="dot" /><span className="label-eyebrow">Why Wakzy</span></div>
          <h2 className="font-heading text-3xl lg:text-5xl mb-12 max-w-3xl">
            <span className="chrome-text">Five reasons</span>{" "}<span className="gold-text">to come see us.</span>
          </h2>
          <div className={`reveal-stagger grid md:grid-cols-2 lg:grid-cols-5 gap-4 ${pillarsVis ? "is-visible" : ""}`}>
            {PILLARS.map((p, i) => (
              <Link key={i} to={p.to} className={`surface kpi-accent ${p.accent} p-6 hover-lift rounded-md block`} data-testid={`pillar-${i}`}>
                <p.icon className="w-7 h-7 mb-4" style={{ color: "var(--accent-color)" }} />
                <div className="font-heading text-lg mb-2">{p.title}</div>
                <p className="text-sm text-slate-400 leading-relaxed">{p.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED INVENTORY */}
      <section className="py-24 border-t border-white/5" ref={invRef}>
        <div className={`max-w-7xl mx-auto px-6 lg:px-8 reveal ${invVis ? "is-visible" : ""}`}>
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <div className="brand-divider max-w-xs mb-4"><span className="dot" /><span className="label-eyebrow">Featured inventory</span></div>
              <h2 className="font-heading text-3xl lg:text-4xl"><span className="chrome-text">This week on the floor.</span></h2>
            </div>
            <Link to="/inventory" className="text-sm text-[#D4AF37] hover:underline">View all inventory →</Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {vehicles.map((v) => (
              <Link key={v.id} to={`/inventory/${v.id}`} className="surface hover-lift rounded-md overflow-hidden block group relative" data-testid={`home-vehicle-${v.id}`}>
                <div className="absolute top-0 left-0 right-0 h-1 z-10 opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: "linear-gradient(90deg,transparent,#e63946 25%,#d4af37 75%,transparent)" }} />
                <div className="aspect-[16/10] bg-cover bg-center relative" style={{ backgroundImage: `url(${v.images?.[0] || "https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=1200"})` }}>
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0b1324] via-transparent to-transparent opacity-80" />
                  <div className="absolute bottom-3 right-3 px-3 py-1.5 rounded font-heading text-lg" style={{ background: "linear-gradient(135deg,rgba(212,175,55,0.95),rgba(230,57,70,0.85))", color: "#0a0a0a", fontWeight: 800 }}>
                    ${v.price.toLocaleString()}
                  </div>
                </div>
                <div className="p-5">
                  <div className="text-xs text-slate-400">{v.year} · {v.color}</div>
                  <h3 className="font-heading text-xl mt-1 mb-2 chrome-text inline-block">{v.make} {v.model}</h3>
                  <div className="flex gap-4 text-xs text-slate-400">
                    <span className="flex items-center gap-1"><Gauge className="w-3 h-3" /> {v.mileage?.toLocaleString()} mi</span>
                    <span className="flex items-center gap-1"><Fuel className="w-3 h-3" /> {v.fuel_type}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* AUCTIONS PREVIEW */}
      {auctions.length > 0 && (
        <section className="py-24 border-t border-white/5" ref={auctionRef}>
          <div className={`max-w-7xl mx-auto px-6 lg:px-8 reveal ${auctionVis ? "is-visible" : ""}`}>
            <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
              <div>
                <div className="brand-divider max-w-xs mb-4"><span className="dot" /><span className="label-eyebrow" style={{ color: "var(--wakzy-red)" }}>Live auctions</span></div>
                <h2 className="font-heading text-3xl lg:text-4xl"><span className="red-text">Bid now.</span> <span className="chrome-text">Drive home today.</span></h2>
              </div>
              <Link to="/auctions" className="text-sm text-[#D4AF37] hover:underline">All auctions →</Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {auctions.map((a) => (
                <Link key={a.id} to={`/auctions/${a.id}`} className="surface hover-lift rounded-md overflow-hidden block">
                  <div className="aspect-[16/9] bg-cover bg-center relative" style={{ backgroundImage: `url(${a.vehicle?.images?.[0] || ""})` }}>
                    <div className="absolute top-3 left-3 pill pill-sold">LIVE</div>
                  </div>
                  <div className="p-5">
                    <div className="text-xs text-slate-400">{a.vehicle?.year}</div>
                    <h3 className="font-heading text-lg chrome-text inline-block">{a.vehicle?.make} {a.vehicle?.model}</h3>
                    <div className="mt-3 flex items-baseline gap-2">
                      <span className="label-eyebrow">Current bid</span>
                      <span className="font-display text-2xl gold-text">${a.current_bid?.toLocaleString()}</span>
                    </div>
                    <div className="text-xs text-slate-500 mt-1">{a.bid_count || 0} bid{a.bid_count === 1 ? "" : "s"}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* REVIEWS */}
      <section className="py-24 border-t border-white/5" ref={revRef}>
        <div className={`max-w-7xl mx-auto px-6 lg:px-8 reveal ${revVis ? "is-visible" : ""}`}>
          <div className="brand-divider max-w-xs mb-4"><span className="dot" /><span className="label-eyebrow">What customers say</span></div>
          <h2 className="font-heading text-3xl lg:text-4xl mb-10 max-w-2xl"><span className="gold-text">The five-star treatment,</span> <span className="chrome-text">every drive.</span></h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {reviews.map((r) => (
              <div key={r.id} className="surface rounded-md p-6">
                <div className="flex gap-0.5 mb-3">
                  {Array.from({ length: r.rating }).map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-[#D4AF37] text-[#D4AF37]" />
                  ))}
                </div>
                <div className="font-heading text-base mb-2">{r.title}</div>
                <p className="text-sm text-slate-400 leading-relaxed">{r.body}</p>
                <div className="text-xs text-slate-500 mt-4">— {r.author}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="py-20 border-t border-white/5" ref={ctaRef}>
        <div className={`max-w-7xl mx-auto px-6 lg:px-8 reveal ${ctaVis ? "is-visible" : ""}`}>
          <div className="gradient-border" style={{ borderRadius: "16px" }}>
            <div className="gradient-border-inner p-10 lg:p-14 flex flex-col lg:flex-row items-center justify-between gap-6" style={{ borderRadius: "15px" }}>
              <div>
                <h3 className="font-heading text-3xl lg:text-4xl mb-2"><span className="chrome-text">Ready to drive home today?</span></h3>
                <p className="text-slate-300 max-w-xl">Reserve any vehicle with a $500 deposit — fully refundable until your paperwork is signed.</p>
              </div>
              <div className="flex flex-wrap gap-3">
                <Button onClick={() => navigate("/inventory")} className="btn-cta rounded-sm h-12 px-8" data-testid="cta-shop-btn">
                  Browse Inventory <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button variant="outline" onClick={() => navigate("/contact")} className="rounded-sm h-12 px-8 border-[#D4AF37] text-[#D4AF37] bg-transparent hover:bg-[#D4AF37]/10">
                  Talk to a Sales Advisor
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </PublicShell>
  );
}

const PILLARS = [
  { icon: Sparkles, title: "Curated Inventory", desc: "Hand-picked luxury vehicles only — every car personally inspected.", to: "/inventory", accent: "kpi-gold" },
  { icon: FileSignature, title: "Easy Financing", desc: "Pre-approved in minutes. Most lenders, one application.", to: "/financing", accent: "kpi-red" },
  { icon: Gavel, title: "Live Auctions", desc: "Dealer & retail auctions, transparent bidding, no surprises.", to: "/auctions", accent: "kpi-navy" },
  { icon: Wrench, title: "Trusted Service", desc: "ASE-certified technicians, OEM-spec parts, complimentary loaner vehicles.", to: "/service", accent: "kpi-emerald" },
  { icon: Star, title: "5-Star Treatment", desc: "Loved by hundreds of customers — read the reviews.", to: "/reviews", accent: "kpi-chrome" },
];
